import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";

const ExportGoogleSheet = () => {
  const [form, setForm] = useState({
    GOOGLE_SHEET_ID: "",
    GOOGLE_CLIENT_EMAIL: "",
    GOOGLE_PRIVATE_KEY: "",
  });

  const [data, setData] = useState([]);
  const [showKey, setShowKey] = useState({});
  const [canAdd, setCanAdd] = useState(true);

  const fetchSheets = async () => {
    try {
      const res = await axios.get("/api/sheet/getall");
      const sheets = res.data.data || [];
      setData(sheets);
      setCanAdd(sheets.length === 0);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSheets();
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canAdd) {
      Swal.fire(
        "Not Allowed",
        "Delete the existing sheet before adding a new one.",
        "warning",
      );
      return;
    }
    try {
      await axios.post("/api/sheet/create", form);
      Swal.fire("Success", "Sheet Added Successfully", "success");
      setForm({
        GOOGLE_SHEET_ID: "",
        GOOGLE_CLIENT_EMAIL: "",
        GOOGLE_PRIVATE_KEY: "",
      });
      fetchSheets();
    } catch (err) {
      const msg = err?.response?.data?.message || "Something went wrong";
      Swal.fire("Error", msg, "error");
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete this sheet?",
      text: "This cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
    });
    if (confirm.isConfirmed) {
      await axios.delete(`/api/sheet/delete/${id}`);
      fetchSheets();
      Swal.fire("Deleted!", "You can now add a new sheet.", "success");
    }
  };

  const handleToggle = async (id, currentStatus) => {
    try {
      setData((prev) =>
        prev.map((item) =>
          item._id === id ? { ...item, isActive: !currentStatus } : item,
        ),
      );
      await axios.put(`/api/sheet/toggle/${id}`);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: currentStatus ? "warning" : "success",
        title: currentStatus ? "Sheet Deactivated 🔴" : "Sheet Activated 🟢",
        showConfirmButton: false,
        timer: 1800,
        timerProgressBar: true,
      });
    } catch (error) {
      Swal.fire("Error", "Toggle failed", "error");
      fetchSheets();
    }
  };

  const toggleKey = (id) => {
    setShowKey((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCopy = (text, label) => {
    if (!text) return;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `${label} copied!`,
          showConfirmButton: false,
          timer: 1500,
          timerProgressBar: true,
        });
      })
      .catch(() => Swal.fire("Error", "Failed to copy", "error"));
  };

  const shortText = (text, len = 25) => {
    if (!text) return "";
    return text.length > len ? text.substring(0, len) + "..." : text;
  };

  const copyBtnStyle = {
    cursor: "pointer",
    fontSize: "15px",
    padding: "2px 4px",
    borderRadius: "4px",
    border: "none",
    background: "transparent",
    transition: "transform 0.2s",
    lineHeight: 1,
  };

  const styles = {
    page: {
      minHeight: "100vh",
      padding: "40px 20px",
      background: "linear-gradient(135deg, #eef2ff 0%, #f8fafc 100%)",
    },
    container: { maxWidth: "1200px", margin: "0 auto" },
    headerWrap: { textAlign: "center", marginBottom: "36px" },
    headerTitle: {
      fontSize: "26px",
      fontWeight: "700",
      color: "#1e1b4b",
      marginBottom: "6px",
    },
    headerSub: { fontSize: "14px", color: "#6b7280" },
    formCard: {
      background: canAdd ? "#ffffff" : "#fafafa",
      borderRadius: "16px",
      border: canAdd ? "1px solid #e0e7ff" : "1px solid #e5e7eb",
      boxShadow: "0 4px 24px rgba(99,102,241,0.07)",
      padding: "28px",
      marginBottom: "28px",
      opacity: canAdd ? 1 : 0.7,
    },
    formLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#6b7280",
      marginBottom: "6px",
      display: "block",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    formInput: {
      width: "100%",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "1.5px solid #e5e7eb",
      fontSize: "13px",
      color: "#111827",
      background: canAdd ? "#f9fafb" : "#f3f4f6",
      outline: "none",
      transition: "border 0.2s",
      cursor: canAdd ? "text" : "not-allowed",
    },
    addBtn: {
      background: canAdd
        ? "linear-gradient(135deg, #6366f1, #4f46e5)"
        : "linear-gradient(135deg, #9ca3af, #6b7280)",
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      padding: "10px 24px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: canAdd ? "pointer" : "not-allowed",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: canAdd ? "0 4px 12px rgba(99,102,241,0.3)" : "none",
    },
    warningBanner: {
      background: "#fffbeb",
      border: "1px solid #fde68a",
      borderRadius: "10px",
      padding: "12px 16px",
      marginBottom: "16px",
      fontSize: "13px",
      color: "#92400e",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    tableCard: {
      background: "#ffffff",
      borderRadius: "16px",
      border: "1px solid #e0e7ff",
      boxShadow: "0 4px 24px rgba(99,102,241,0.07)",
      overflow: "hidden",
    },
    thead: { background: "linear-gradient(90deg, #f0f4ff, #f8f0ff)" },
    th: {
      padding: "14px 20px",
      fontSize: "12px",
      fontWeight: "700",
      color: "#6366f1",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      borderBottom: "2px solid #e0e7ff",
      whiteSpace: "nowrap",
    },
    emptyRow: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#9ca3af",
      fontSize: "14px",
    },
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* HEADER */}
        <div style={styles.headerWrap}>
          <h2 style={styles.headerTitle}>📊 Export Sheet Manager</h2>
          <p style={styles.headerSub}>
            Only one Google Sheet can be active at a time
          </p>
        </div>

        {/* FORM CARD */}
        <div style={styles.formCard}>
          {/* ✅ Warning Banner — shown when a sheet already exists */}
          {!canAdd && (
            <div style={styles.warningBanner}>
              ⚠️ <strong>A sheet already exists.</strong>&nbsp; Delete the
              existing sheet below before adding a new one.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-12 col-md-4">
                <label style={styles.formLabel}>Sheet ID</label>
                <input
                  name="GOOGLE_SHEET_ID"
                  value={form.GOOGLE_SHEET_ID}
                  onChange={handleChange}
                  style={styles.formInput}
                  placeholder="Enter Google Sheet ID"
                  required
                  disabled={!canAdd} // ✅ Disabled when sheet exists
                  onFocus={(e) =>
                    canAdd && (e.target.style.border = "1.5px solid #6366f1")
                  }
                  onBlur={(e) =>
                    (e.target.style.border = "1.5px solid #e5e7eb")
                  }
                />
              </div>
              <div className="col-12 col-md-4">
                <label style={styles.formLabel}>Client Email</label>
                <input
                  name="GOOGLE_CLIENT_EMAIL"
                  value={form.GOOGLE_CLIENT_EMAIL}
                  onChange={handleChange}
                  style={styles.formInput}
                  placeholder="service@project.iam.gserviceaccount.com"
                  required
                  disabled={!canAdd}
                  onFocus={(e) =>
                    canAdd && (e.target.style.border = "1.5px solid #6366f1")
                  }
                  onBlur={(e) =>
                    (e.target.style.border = "1.5px solid #e5e7eb")
                  }
                />
              </div>
              <div className="col-12 col-md-4">
                <label style={styles.formLabel}>Private Key</label>
                <input
                  name="GOOGLE_PRIVATE_KEY"
                  value={form.GOOGLE_PRIVATE_KEY}
                  onChange={handleChange}
                  style={styles.formInput}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----"
                  required
                  disabled={!canAdd}
                  onFocus={(e) =>
                    canAdd && (e.target.style.border = "1.5px solid #6366f1")
                  }
                  onBlur={(e) =>
                    (e.target.style.border = "1.5px solid #e5e7eb")
                  }
                />
              </div>
            </div>
            <div className="d-flex justify-content-end mt-4">
              <button
                type="submit"
                style={styles.addBtn}
                disabled={!canAdd}
                onMouseEnter={(e) => {
                  if (!canAdd) return;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 6px 20px rgba(99,102,241,0.4)";
                }}
                onMouseLeave={(e) => {
                  if (!canAdd) return;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 12px rgba(99,102,241,0.3)";
                }}
              >
                {canAdd ? "➕ Add Sheet" : "🔒 Sheet Exists"}
              </button>
            </div>
          </form>
        </div>

        {/* TABLE CARD */}
        <div style={styles.tableCard}>
          <div className="table-responsive">
            <table
              className="table align-middle mb-0"
              style={{ minWidth: "750px" }}
            >
              <thead style={styles.thead}>
                <tr>
                  {[
                    "Client Email",
                    "Sheet ID",
                    "Private Key",
                    "Status",
                    "Action",
                  ].map((h) => (
                    <th key={h} style={styles.th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={styles.emptyRow}>
                      <div style={{ fontSize: "32px", marginBottom: "10px" }}>
                        📭
                      </div>
                      No sheets added yet. Fill the form above to get started.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr
                      key={item._id}
                      style={{
                        transition: "all 0.25s ease",
                        cursor: "pointer",
                        borderLeft: "3px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f5f3ff";
                        e.currentTarget.style.borderLeft = "3px solid #6366f1";
                        e.currentTarget.style.transform = "translateX(3px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "";
                        e.currentTarget.style.borderLeft =
                          "3px solid transparent";
                        e.currentTarget.style.transform = "translateX(0)";
                      }}
                    >
                      {/* CLIENT EMAIL */}
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: "13px",
                                color: "#111827",
                              }}
                            >
                              {shortText(item.GOOGLE_CLIENT_EMAIL, 30)}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#9ca3af",
                                marginTop: "2px",
                              }}
                            >
                              Service Account
                            </div>
                          </div>
                          <span
                            onClick={() =>
                              handleCopy(
                                item.GOOGLE_CLIENT_EMAIL,
                                "Client Email",
                              )
                            }
                            style={copyBtnStyle}
                            title="Copy"
                          >
                            📋
                          </span>
                        </div>
                      </td>

                      {/* SHEET ID */}
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: "12px",
                              background: "#f3f4f6",
                              color: "#374151",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            {shortText(item.GOOGLE_SHEET_ID, 20)}
                          </span>
                          <span
                            onClick={() =>
                              handleCopy(item.GOOGLE_SHEET_ID, "Sheet ID")
                            }
                            style={copyBtnStyle}
                            title="Copy"
                          >
                            📋
                          </span>
                        </div>
                      </td>

                      {/* PRIVATE KEY */}
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontSize: "12px",
                              color: "#6b7280",
                              background: "#f9fafb",
                              padding: "4px 8px",
                              borderRadius: "6px",
                              border: "1px solid #e5e7eb",
                              maxWidth: showKey[item._id] ? "220px" : "auto",
                              wordBreak: showKey[item._id]
                                ? "break-all"
                                : "normal",
                              whiteSpace: showKey[item._id]
                                ? "normal"
                                : "nowrap",
                            }}
                          >
                            {showKey[item._id]
                              ? item.GOOGLE_PRIVATE_KEY
                              : "••••••••••••••••••••"}
                          </span>
                          <span
                            onClick={() => toggleKey(item._id)}
                            style={copyBtnStyle}
                            title={showKey[item._id] ? "Hide" : "Show"}
                          >
                            {showKey[item._id] ? "🙈" : "👁️"}
                          </span>
                          <span
                            onClick={() =>
                              handleCopy(item.GOOGLE_PRIVATE_KEY, "Private Key")
                            }
                            style={copyBtnStyle}
                            title="Copy"
                          >
                            📋
                          </span>
                        </div>
                      </td>

                      {/* STATUS */}
                      <td style={{ padding: "16px 20px" }}>
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "5px",
                            padding: "5px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                            background: item.isActive ? "#dcfce7" : "#f3f4f6",
                            color: item.isActive ? "#15803d" : "#6b7280",
                            border: item.isActive
                              ? "1px solid #bbf7d0"
                              : "1px solid #e5e7eb",
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              background: item.isActive ? "#22c55e" : "#9ca3af",
                              display: "inline-block",
                            }}
                          />
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* ACTION */}
                      <td style={{ padding: "16px 20px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                          }}
                        >
                          <div className="form-check form-switch m-0">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              style={{
                                cursor: "pointer",
                                width: "40px",
                                height: "20px",
                              }}
                              checked={item.isActive}
                              onChange={() =>
                                handleToggle(item._id, item.isActive)
                              }
                            />
                          </div>
                          <button
                            onClick={() => handleDelete(item._id)}
                            style={{
                              background: "#fff0f0",
                              border: "1px solid #fecaca",
                              color: "#dc2626",
                              borderRadius: "8px",
                              padding: "6px 12px",
                              fontSize: "13px",
                              cursor: "pointer",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              transition: "all 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = "#dc2626";
                              e.currentTarget.style.color = "#fff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = "#fff0f0";
                              e.currentTarget.style.color = "#dc2626";
                            }}
                          >
                            <i className="ri-delete-bin-fill"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportGoogleSheet;
